<template>
  <div v-if="activeChannel" class="floating-chat-window" :class="`role-${userRole}`">
    <div
      class="chat-header flex justify-content-between align-items-center p-3 text-white border-round-top-lg shadow-2">
      <div class="flex align-items-center gap-2">
        <img :src="blankProfile" class="w-2rem h-2rem border-circle" />
        <span class="font-bold text-sm">{{getDisplayName(activeChannel)}}</span>
        <!-- <span class="font-bold text-sm">{{ activeChannel.data.name || 'Chat' }}</span> -->
      </div>
      <Button icon="pi pi-times" class="p-button-rounded p-button-text p-button-sm close-btn" @click="closeChat" />
    </div>

    <div class="chat-body p-3 overflow-y-auto" ref="chatBody">
      <div v-for="message in messages" :key="message.id" class="message-wrapper">
        <div 
          class="message-container"
          :class="{'sent-message': message.user.id === currentUserId, 'received-message': message.user.id !== currentUserId}"
        >
          <img :src="blankProfile" class="w-2rem h-2rem border-circle avatar" />
          
          <div class="message-bubble p-3 border-round-lg shadow-1 text-sm relative">
            <Button 
                v-if="message.user.id === currentUserId && !message.temp" 
                icon="pi pi-trash" 
                class="p-button-rounded p-button-plain p-button-xs delete-btn"
                @click="deleteMessage(message.id)"
            />

            <div v-if="message.text" class="message-text" :class="{'opacity-50': message.temp}">
                {{ message.text }}
                <span v-if="message.temp" class="text-xs ml-1">...</span>
            </div>
            
            <div v-if="message.attachments && message.attachments.length > 0" class="attachments mt-2">
              <div v-for="attachment in message.attachments" :key="attachment.url" class="mb-2">
                
                <a v-if="attachment.type === 'image'" :href="attachment.image_url || attachment.url" target="_blank">
                    <img :src="attachment.thumb_url || attachment.url" class="attachment-image border-round" />
                </a>
                
                <video v-else-if="attachment.type === 'video'" controls class="attachment-video border-round">
                  <source :src="attachment.url" type="video/mp4">
                </video>
                
                <a v-else :href="attachment.url" target="_blank" download class="attachment-file flex align-items-center gap-2 p-2 border-round surface-hover">
                  <i class="pi pi-file p-p-2 border-circle bg-primary-reverse text-primary"></i>
                  <div class="flex flex-column">
                      <span class="font-bold text-xs text-overflow-ellipsis overflow-hidden white-space-nowrap" style="max-width: 150px;">
                        {{ attachment.title || 'File' }}
                      </span>
                      <span class="text-xs opacity-70">
                          {{ attachment.file_size_formatted }}
                      </span>
                  </div>
                  <i class="pi pi-download ml-auto opacity-70"></i>
                </a>
              </div>
            </div>
            
            <small class="message-meta text-xs opacity-70 block mt-1">
              {{ formatTime(message.created_at) }}
            </small>
          </div>
        </div>
      </div>
    </div>

    <div class="chat-input p-3 border-top-1 surface-border bg-white">
      <div class="flex gap-2 align-items-center">
        <input type="file" ref="fileInput" multiple @change="handleFileUpload" class="hidden" />
        
        <Button 
            :icon="isUploading ? 'pi pi-spin pi-spinner' : 'pi pi-paperclip'"
            class="p-button-rounded p-button-text flex-shrink-0" 
            @click="$refs.fileInput.click()" 
            :disabled="isUploading"
        />
        
        <input v-model="newMessage" @keyup.enter="sendMessage()" type="text"
          class="flex-1 min-w-0 p-inputtext p-component p-inputtext-sm" placeholder="Type a message..." :disabled="isUploading" />

        <Button icon="pi pi-send" class="p-button-rounded p-button-text flex-shrink-0" @click="sendMessage()" :disabled="!newMessage.trim() || isUploading" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, defineProps, defineEmits, computed } from 'vue';
import Button from 'primevue/button';
import { chatClient } from '@/stores/authStore';
import { useAuthStore } from '@/stores/authStore';
import blankProfile from '@/assets/blank-profile.png';

const props = defineProps({
  activeChannel: { type: Object, default: null },
});

const emit = defineEmits(['close']);
const authStore = useAuthStore();
const fileInput = ref(null);

const messages = ref([]);
const newMessage = ref('');
const chatBody = ref(null);
const isUploading = ref(false);

const currentUserId = computed(() => chatClient.userID);
const userRole = computed(() => authStore.user?.role || 'student');

const getDisplayName = (channel) => {
  try{
  if (channel.data.name && !channel.data.name.startsWith('Chat with')) {
    return channel.data.name;
  }
  
  const otherMember = Object.values(channel.state.members).find(
    (member) => member.user.id !== currentUserId.value
  );
  
  return otherMember ? `Chat with ${otherMember.user.name}` : 'Unknown User';
} catch (error) {
  console.log(error);
  return 'Unknown User';
}
};

watch(() => props.activeChannel, async (newChannel) => {
  if (newChannel) {
    try{
    await newChannel.watch();
    messages.value = newChannel.state.messages;
    scrollToBottom();

    newChannel.on('message.new', (event) => {
        const existingIndex = messages.value.findIndex(m => m.id === event.message.id);
        if(existingIndex > -1) {
            messages.value[existingIndex] = event.message;
        } else {
            messages.value.push(event.message);
        }
      scrollToBottom();
    });
  } catch (error) {
    console.log(error);
  }
  }
}, { immediate: true });

const sendMessage = async (attachments = [], tempMessage = null) => {
  try{
  if ((newMessage.value.trim() || attachments.length > 0) && props.activeChannel) {
    if(tempMessage) {
        messages.value.push(tempMessage);
        scrollToBottom();
    }

    await props.activeChannel.sendMessage({ 
      text: newMessage.value,
      attachments: attachments 
    });
    newMessage.value = '';
    scrollToBottom();
  }
} catch (error) {
  console.log(error);
}
};

const handleFileUpload = async (event) => {
  try{
  const files = event.target.files;
  if (!files.length) return;

  isUploading.value = true;
  const attachments = [];

  for (let file of files) {
    try {
      const response = await props.activeChannel.sendFile(file);
      
      const fileSize = (file.size / 1024).toFixed(1) + ' KB';

      attachments.push({
        type: file.type.startsWith('image') ? 'image' : file.type.startsWith('video') ? 'video' : 'file',
        url: response.file,
        title: file.name,
        file_size_formatted: fileSize
      });
    } catch (error) {
      console.error("File upload failed", error);
    }
  }

  const tempMessage = {
      id: 'temp-' + Date.now(),
      text: newMessage.value,
      attachments: attachments,
      user: { id: currentUserId.value, image: authStore.profileImageUrl },
      created_at: new Date(),
      temp: true
  };

  await sendMessage(attachments, tempMessage);
  fileInput.value.value = '';
  isUploading.value = false;
} catch (error) {
  console.log(error);
}
};

const deleteMessage = async (messageId) => {
    try {
        await chatClient.deleteMessage(messageId);
        messages.value = messages.value.filter(m => m.id !== messageId);
    } catch (error) {
        console.error("Delete failed", error);
    }
}

const closeChat = () => emit('close');
const formatTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  if (isToday) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
   + ', ' +date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};
const scrollToBottom = () => {
  nextTick(() => {
    if (chatBody.value) chatBody.value.scrollTop = chatBody.value.scrollHeight;
  });
};
</script>

<style scoped>
.floating-chat-window {
  position: fixed;
  bottom: 100px;
  right: 20px;
  width: 380px;
  height: min(580px, calc(100vh - 120px));
  background: white;
  border-radius: 16px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 1000;
  border: 1px solid var(--surface-border);
}

/* Bottom-anchored layouts only — from 768px up the panel docks to the top instead. */
@media (max-width: 767px) and (max-height: 700px) {
  .floating-chat-window {
    bottom: 20px;
    height: calc(100vh - 40px);
  }
}

@media (max-width: 600px) {
  .floating-chat-window {
    bottom: 0;
    right: 0;
    width: 100%;
    height: 100%;
    border-radius: 0;
  }
}

/* Position only — height stays on the base rule above. */
@media (min-width: 768px) {
  .floating-chat-window {
    top: calc(10.5vh + 15px);
    bottom: auto;
    right: 40px;
  }
}

.chat-body {
  flex: 1;
  background-color: var(--surface-ground);
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.chat-body::-webkit-scrollbar {
  display: none;
}

.message-wrapper {
  display: flex;
  width: 100%;
  margin-bottom: 12px;
}

.received-message {
  display: flex;
  flex-direction: row;
  margin-right: auto;
  align-items: flex-end;
  gap: 8px;
  max-width: 80%;
}

.sent-message {
  display: flex;
  flex-direction: row-reverse;
  margin-left: auto;
  align-items: flex-end;
  gap: 8px;
  max-width: 80%;
}

.message-bubble {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  max-width: 100%;
  word-wrap: break-word;
}

.chat-input {
  background-color: #f9f9f9;
  border-top: 1px solid var(--surface-border);
  padding: 0.5rem 1rem !important;
}

.chat-input input[type="text"] {
  border-radius: 20px;
  border: 1px solid #ddd;
  padding: 0.75rem 1rem;
  transition: border-color 0.2s;
}

.chat-input input[type="text"]:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 1px var(--primary-color);
}

.delete-btn {
    position: absolute;
    top: 2px;
    right: 2px;
    opacity: 0;
    transition: all 0.2s;
    color: rgba(255, 255, 255, 0.7) !important;
    width: 28px !important;
    height: 28px !important;
    margin: 5px !important;
    padding: 1px 0 0 0 !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
}

.sent-message .message-bubble:hover .delete-btn {
    opacity: 1;
}

.role-student .delete-btn {
    background-color: #e64260 !important;
    border: #e64260 !important;
}

.role-tutor .delete-btn {
    background-color: #009696 !important;
    border: #009696 !important;
}

.delete-btn:hover {
    background-color: white !important;
    color: #A72E44 !important;
}

.role-student .sent-message .message-bubble {
  background: linear-gradient(360deg, #FFB8C5 -35.88%, #A72E44 100%) !important;
  color: white;
  border-bottom-right-radius: 0px !important;
}

.role-tutor .sent-message .message-bubble {
  background: linear-gradient(360deg, #77D8D8 -35.88%, #005F5F 100%) !important;
  color: white;
  border-bottom-right-radius: 0px !important;
}

.role-student .received-message .message-bubble {
  background: linear-gradient(360deg, #77D8D8 -35.88%, #005F5F 100%) !important;
  color: white;
  border-bottom-left-radius: 0px !important;
}

.role-tutor .received-message .message-bubble {
  background: linear-gradient(360deg, #FFB8C5 -35.88%, #A72E44 100%) !important;
  color: white;
  border-bottom-left-radius: 0px !important;
}

.role-student .chat-header {
  background: linear-gradient(360deg, #FFB8C5 -35.88%, #A72E44 100%) !important;
}

.role-tutor .chat-header {
  background: linear-gradient(360deg, #77D8D8 -35.88%, #005F5F 100%) !important;
}

.role-student .chat-input .p-button {
  background: linear-gradient(360deg, #FFB8C5 -35.88%, #A72E44 100%) !important;
  color: white !important;
  border: none;
}

.role-tutor .chat-input .p-button {
  background: linear-gradient(360deg, #77D8D8 -35.88%, #005F5F 100%) !important;
  color: white !important;
  border: none;
}

.chat-input .p-button:hover {
  opacity: 0.9;
}

.attachment-image {
  max-width: 200px;
  max-height: 200px;
  object-fit: cover;
  display: block;
}

.attachment-video {
  max-width: 200px;
  max-height: 200px;
  display: block;
}

.attachment-file {
  text-decoration: none;
  color: var(--text-color);
  border: 1px solid var(--surface-border);
  background: rgba(0,0,0,0.03);
}

.avatar {
  flex-shrink: 0;
}

.close-btn {
  color: white !important;
}

.role-student .close-btn:hover {
  color: var(--orange-500) !important;
}

.role-tutor .close-btn:hover {
  color: var(--teal-500) !important;
}
</style>